import api from './api';

export interface User {
  id: number;
  email: string;
  full_name: string;
  role_id?: number;
  role_name?: string;
  role_code?: string;
  phone?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Role {
  id: number;
  code: string;
  name: string;
  description?: string;
  level?: number;
}

export interface ProcessType {
  id: number;
  name: string;
  code: string;
  description?: string;
  stages?: Stage[];
}

export interface Stage {
  id: number;
  process_type_id: number;
  name: string;
  order_index: number;
  estimated_days?: number;
  legal_term_days?: number;
  requires_checklist: boolean;
  is_final: boolean;
}

export interface CreateUserDto {
  email: string;
  password: string;
  full_name: string;
  role_id?: number;
  phone?: string;
}

export interface DashboardStats {
  cases: {
    total: number;
    active: number;
    suspended: number;
    closed: number;
    urgent: number;
  };
  tasks: {
    total: number;
    pending: number;
    in_progress: number;
    completed: number;
    overdue: number;
  };
  hearings: {
    total: number;
    today: number;
    this_week: number;
  };
  users: {
    total: number;
    active: number;
  };
}

export interface LawyerStats {
  lawyer_id: number;
  lawyer_name: string;
  total_cases: number;
  active_cases: number;
  closed_cases: number;
  suspended_cases: number;
  green_cases: number;
  yellow_cases: number;
  red_cases: number;
  compliance_percentage: number;
}

export interface ComplianceReport {
  lawyer_id: number;
  lawyer_name: string;
  total_cases: number;
  completed_tasks: number;
  pending_tasks: number;
  overdue_tasks: number;
  task_compliance: number;
}

const adminService = {
  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await api.get('/admin/dashboard');
    return response.data.data;
  },

  // Users
  async listUsers(): Promise<User[]> {
    const response = await api.get('/admin/users');
    return response.data.data || [];
  },

  async getUserById(id: number): Promise<User> {
    const response = await api.get(`/admin/users/${id}`);
    return response.data.data || response.data;
  },

  async createUser(data: CreateUserDto): Promise<User> {
    const response = await api.post('/admin/users', data);
    return response.data.data || response.data;
  },

  async updateUser(id: number, data: Partial<CreateUserDto>): Promise<User> {
    const response = await api.patch(`/admin/users/${id}`, data);
    return response.data.data || response.data;
  },

  async toggleUserStatus(id: number): Promise<User> {
    const response = await api.post(`/admin/users/${id}/toggle-status`);
    return response.data.data || response.data;
  },

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/admin/users/${id}`);
  },

  // Roles
  async listRoles(): Promise<Role[]> {
    const response = await api.get('/admin/roles');
    return response.data.data || [];
  },

  // Process Types
  async listProcessTypes(): Promise<ProcessType[]> {
    const response = await api.get('/admin/process-types');
    return response.data.data || [];
  },

  async getProcessTypeById(id: number): Promise<ProcessType> {
    const response = await api.get(`/admin/process-types/${id}`);
    return response.data.data || response.data;
  },

  async getStagesByProcessType(processTypeId: number): Promise<Stage[]> {
    const response = await api.get(`/admin/process-types/${processTypeId}/stages`);
    return response.data.data || [];
  },

  // Reports
  async getLawyerStats(): Promise<LawyerStats[]> {
    const response = await api.get('/admin/reports/lawyers');
    return response.data.data || [];
  },

  async getCasesByStatus(): Promise<any[]> {
    const response = await api.get('/admin/reports/cases-by-status');
    return response.data.data || [];
  },

  async getCasesByLawyer(lawyerId?: number): Promise<any[]> {
    const params = lawyerId ? { lawyer_id: lawyerId } : {};
    const response = await api.get('/admin/reports/cases-by-lawyer', { params });
    return response.data.data || [];
  },

  async getComplianceReport(): Promise<ComplianceReport[]> {
    const response = await api.get('/admin/reports/compliance');
    return response.data.data || [];
  }
};

export default adminService;
